/**
 * "Is this string a tester token?" — one definition, shared by the widget, the
 * server and the dashboard.
 *
 * It exists because a tester pasted their token into the panel's *session name*
 * box, and a session name is DISPLAY text: it renders in the panel footer, in
 * the session picker, in the dashboard's progress tabs and on every finding
 * card. A working credential ended up on screen in four places, which is the
 * same class of mistake inviolable rule 9 guards against for the issue-tracker
 * credential — the token belongs in its own field and nowhere else.
 *
 * Deliberately shape-based rather than a lookup: the check has to work in the
 * widget (which cannot hash or query anything) and it has to catch a token that
 * has since been revoked, because publishing a dead credential is still
 * publishing one. The prefix is the reason `mintToken` uses a greppable one.
 *
 * Vanilla TS with no imports, so the widget bundle can take it (rule 6).
 */

/** Prefix every minted tester token carries. `server/util.ts` mints with it. */
export const TESTER_TOKEN_PREFIX = "qad_";

/**
 * A minted token is the prefix plus 32 random bytes base64url-encoded (43
 * chars), so anything prefixed and long enough to be one is treated as one.
 * Erring towards false positives is the right direction here: the cost is a
 * tester renaming a session, and the cost of a miss is a live credential on
 * screen.
 */
const MIN_TOKEN_BODY = 20;

export function looksLikeTesterToken(
  value: string | null | undefined,
): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  return (
    trimmed.startsWith(TESTER_TOKEN_PREFIX) &&
    trimmed.length >= TESTER_TOKEN_PREFIX.length + MIN_TOKEN_BODY &&
    // No spaces: "qad_ token for the sweep" is prose, not a credential.
    !/\s/.test(trimmed)
  );
}
